import FormBuilderModel from "../../../../common/models/tracked/formBuilderModel";
import Guid from "~/helpers/guid";
import {NotificationTriggerType} from "./notificationTriggerType";
import Recipient from "../workflow/recipient";
import {NotificationRelativeTriggerLabel, NotificationTriggerLabel} from "./notificationTriggerLabel";
import {WorkflowType} from "~/enums/forms/workflowSteps/workflowType";
import WorkflowStep from "../workflow/workflowStep";
import RecipientFactory from "./recipientFactory";
import {ObjectHelper} from "../../../../common/helpers/objectHelper";
import NotificationTrigger from "./notificationTrigger";
import {RelationType} from "../../../../common/models/relationType";
import {DurationUnit} from "../../../../common/enums/durationUnit";
import {NotificationDeliveryMethod} from "~/enums/notificationDeliveryMethod";
import IRepo from "../../../../common/interfaces/repo";
import Repo from "../../../../common/state/modules/repo";
import {InstanceHelper} from "../../../../common/helpers/instanceHelper";
import {ArrayHelper} from "../../../../common/helpers/arrayHelper";
import {NotificationRelativeToType} from "./notificationRelativeToType";
import {RecipientType} from "../../../../common/enums/recipentType";
import {INotification} from "../../../../common/interfaces/notificationInterfaces";
import {PermitteeType} from "~/enums/permitteeType";
import {DefaultEmailStyle} from "~/enums/defaultEmailStyle";

export default class Notification extends FormBuilderModel  implements INotification {
  client_id: string = Guid.create();
  trigger_on: NotificationTriggerType = null;
  target_action_client_id: string;
  workflowStep: WorkflowStep;
  recipients: { [key: string]: Recipient } = {};
  subject: string = '';
  body: string = '';
  is_after: boolean;
  relative_to: NotificationRelativeToType;
  relative_amount: number;
  relative_unit: DurationUnit;
  delivery_methods: NotificationDeliveryMethod[] = [NotificationDeliveryMethod.ViaInSystem];
  except = ['form', 'recipients', 'createEmbeddedObject', 'workflowStep'];
  include_invoice: boolean;
  email_style: DefaultEmailStyle = DefaultEmailStyle.Default

  static repo: IRepo<Notification> = new Repo<Notification>('notifications', Notification);

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'recipients': { type: Recipient, relationType: RelationType.dictionary }
    };
  }

  createEmbeddedObject = {
    recipients: (parent, json): Recipient => { return RecipientFactory.create(parent, false, json.recipient_type);}
  };

  static get triggerTypes(): number[] {
    return (<any>Object).values(NotificationTriggerType).filter(k => !isNaN(Number(k)) );
  }

  static triggerOptions(workflowStep: WorkflowStep): {label: string, value: NotificationTrigger}[] {
    const allowedTypes = [];
    let includeActions = false;

    if (workflowStep.workflow_type === WorkflowType.initialStep) {
      allowedTypes.push(NotificationTriggerType.initialSubmission);
      includeActions = true;
    } else if (workflowStep.has_approval) {
      allowedTypes.push(
          NotificationTriggerType.beginning,
      );
      if (workflowStep.has_form_section) {
        allowedTypes.push(NotificationTriggerType.formSectionCompletion);
      } else {
        allowedTypes.push(NotificationTriggerType.completion);
      }
      includeActions = true;
    } else if (workflowStep.has_form_section) {
      allowedTypes.push(
          NotificationTriggerType.beginning,
          NotificationTriggerType.completion
      );
      includeActions = true;
    } else if (workflowStep.workflow_type === WorkflowType.form) {
      allowedTypes.push(
          NotificationTriggerType.beginning,
          NotificationTriggerType.externalFormCompletion
      );
    } else if (workflowStep.workflow_type === WorkflowType.payment) {
      allowedTypes.push(
          NotificationTriggerType.beginning,
          NotificationTriggerType.payment,
      );
      // Only display Check Notification Trigger if Check or Online Payment or Check is current selected Form of Payment
      if (workflowStep.isCheckOrOnlinePaymentOrCheck) {
        allowedTypes.push(NotificationTriggerType.check);
      }
    } else if (workflowStep.workflow_type === WorkflowType.licenseStatusChange) {
      allowedTypes.push(NotificationTriggerType.completion);
    }

    allowedTypes.push(NotificationTriggerType.relative);

    if (workflowStep.has_sla ) {
      allowedTypes.push(NotificationTriggerType.sla);
    }

    if (workflowStep.isCompleterStep && workflowStep.submitter.reference_type === PermitteeType.group) {
      allowedTypes.push(NotificationTriggerType.assignment);
    }

    const triggers = Notification.triggerTypes
      .filter(type => allowedTypes.includes(type))
      .map(t => {
        return {label: NotificationTriggerLabel[t], value: new NotificationTrigger(t, null)};
      });

    if (includeActions && Object.keys(workflowStep.actions).length > 1) {
      for (const clientId in workflowStep.actions) {
        const action = workflowStep.actions[clientId];
        triggers.push({label: `On ${action.title}`, value: new NotificationTrigger(NotificationTriggerType.action, clientId)});
      }
    }

    return triggers;
  }

  get trigger(): NotificationTrigger {
    return new NotificationTrigger(
      this.trigger_on,
      this.target_action_client_id
    );
  }

  set trigger(val: NotificationTrigger) {
    this.setTriggerOn(val.triggerOn);
    this.target_action_client_id = val.targetClientId;
  }

  get hasRelativeTrigger(): boolean {
    return [NotificationTriggerType.sla, NotificationTriggerType.relative].includes(this.trigger_on);
  }

  get rootName(): string {
    return 'notifications';
  }

  get isCollectionRoot(): boolean {
    return true;
  }

  get childRootName(): string {
    return 'recipients';
  }

  get isValid(): boolean {
    return this.validate('trigger_on', 'toRecipientsArray', 'subject', 'body', 'delivery_methods');
  }

  validate(...props): boolean {
    let valid = true;
    for (let i = 0; i < props.length; i++) {
      const prop = props[i];
      switch (prop) {
        case 'trigger_on':
          valid = Notification.triggerTypes.includes(this[prop]);
          break;
        case 'toRecipientsArray':
        case 'delivery_methods':
          valid = InstanceHelper.isPresent(this[prop]);
          break;
        case 'subject':
        case 'body':
          valid = this[prop].trim().length > 0;
          break;
        default:
          break;
      }
      if (!valid) {
        break;
      }
    }
    return valid;
  }

  get displayTriggerString(): string {
    let notificationLabel;
    if (this.hasRelativeTrigger) {
      notificationLabel = this.relative_amount.toString(10) + ' ';
      notificationLabel += DurationUnit[this.relative_unit] + (this.relative_amount === 1 ? ' ' : 's ');
      notificationLabel += this.is_after ? 'after ' : 'before ';
      notificationLabel += NotificationRelativeTriggerLabel[this.relative_to].toLowerCase();
    } else {
      notificationLabel = NotificationTriggerLabel[this.trigger_on].toLowerCase();
    }
    if (this.target_action_client_id) {
      const action = this.workflowStep.actions[this.target_action_client_id];
      return notificationLabel.replace('$actionname', action ? action.title : '');
    }
    return notificationLabel;
  }

  get recipientsDisplayNames(): string[] {
    return this.recipientsArray.map(r => r.display);
  }

  get recipientsToString(): string {
    return ArrayHelper.toSentence(this.recipientsDisplayNames);
  }

  get recipientsArray(): Recipient[] {
    return ObjectHelper.toArray(this.recipients);
  }

  get toRecipientsArray(): Recipient[] {
    return this.recipientsArray.filter(r => r.recipient_type === RecipientType.to);
  }

  get ccRecipientsArray(): Recipient[] {
    return this.recipientsArray.filter(r => r.recipient_type === RecipientType.cc);
  }

  setTriggerOn(val: NotificationTriggerType): void {
    switch (val) {
      case NotificationTriggerType.sla:
        if (![NotificationRelativeToType.slaDeadline, NotificationRelativeToType.slaGoal].includes(this.relative_to)) {
          // set default relative_to for sla if choosing sla trigger type
          this.relative_to = NotificationRelativeToType.slaDeadline;
        }
        this.is_after = false;
        this.relative_amount = 1;
        break;
      case NotificationTriggerType.relative:
        if (![NotificationRelativeToType.stepStart, NotificationRelativeToType.stepEnd].includes(this.relative_to)) {
          // set default relative_to for relative to step if choosing sla trigger type
          this.relative_to = NotificationRelativeToType.stepStart;
        }
        this.is_after = true;
        this.relative_amount = 1;
        break;
      default:
        this.relative_to = null;
        this.is_after = null;
    }

    this.trigger_on = val;
  }

  get hasEmailDeliveryMethod(): boolean {
    return this.delivery_methods.includes(NotificationDeliveryMethod.ViaEmail);
  }

  setDeliveryMethods(methods: NotificationDeliveryMethod[]) {
    if (!methods.includes(NotificationDeliveryMethod.ViaEmail)) {
      this.ccRecipientsArray.forEach(r => r.destroy());
    }
    this.delivery_methods = methods;
  }
}